import { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  listOwnedZones, saveOwnedZone, deleteOwnedZone, clearOwnedZones,
} from "../lib/api";
import { getToken } from "../lib/api";

// Legacy localStorage keys — read once on first load to migrate existing data.
const ZONES_KEY    = "landhep_owned_zones";
const PAID_KEY     = "landhep_pro_paid_grids";
const AI_CACHE_KEY = "landhep_ai_cache_pro_v8";

function purgeLegacyAICache(gridId) {
  try {
    const store = JSON.parse(localStorage.getItem(AI_CACHE_KEY) || "{}");
    const prefix = `__${gridId}__`;
    Object.keys(store).filter((k) => k.includes(prefix)).forEach((k) => delete store[k]);
    localStorage.setItem(AI_CACHE_KEY, JSON.stringify(store));
  } catch {}
}

// grid_id is a BIGINT, so the API returns it as a number; the map, the comparison
// bucket and the Deep Analysis page all carry it as a string. Normalise here, once,
// so every `ownedIds.includes(id)` compares like with like — a number never equals
// its string, and owned grids silently showed as unpaid.
const withStringIds = (zones) => (zones || []).map((z) => ({ ...z, gridId: String(z.gridId) }));

const OwnedGridsContext = createContext(null);

export function OwnedGridsProvider({ children }) {
  const [ownedZones, setOwnedZones] = useState([]);
  const [loaded,     setLoaded]     = useState(false);

  // Initial load from DB (falls back to localStorage when not authed).
  useEffect(() => {
    async function load() {
      if (!getToken()) {
        // Not logged in — use localStorage
        try {
          const all = JSON.parse(localStorage.getItem(ZONES_KEY) || "[]");
          setOwnedZones(withStringIds(all));
        } catch {}
        setLoaded(true);
        return;
      }

      try {
        const zones = await listOwnedZones();
        setOwnedZones(withStringIds(zones));
        // The old "push legacy localStorage zones into an empty DB" migration is
        // gone: on a shared browser it adopted the PREVIOUS user's zones into
        // whichever account logged in next. Owned zones live in the DB only.
        localStorage.removeItem(ZONES_KEY);
        localStorage.removeItem(PAID_KEY);
      } catch {
        // Offline or 401 — fall back to localStorage
        try {
          const all = JSON.parse(localStorage.getItem(ZONES_KEY) || "[]");
          setOwnedZones(withStringIds(all));
        } catch {}
      } finally {
        setLoaded(true);
      }
    }
    load();
  }, []);

  const refresh = useCallback(async () => {
    if (!getToken()) return;
    try {
      const zones = await listOwnedZones();
      setOwnedZones(withStringIds(zones));
    } catch {}
  }, []);

  const saveZone = useCallback(async (zone) => {
    if (!getToken()) {
      // Offline fallback — localStorage only
      try {
        const fp  = _profileFp();
        const all = JSON.parse(localStorage.getItem(ZONES_KEY) || "[]")
          .filter((z) => !(z._fp === fp && z.gridId === zone.gridId));
        all.unshift({ ...zone, _fp: fp });
        localStorage.setItem(ZONES_KEY, JSON.stringify(all));
        setOwnedZones(withStringIds(all));
      } catch {}
      return;
    }
    try {
      await saveOwnedZone(zone);
      await refresh();
    } catch {}
  }, [refresh]);

  const removeZone = useCallback(async (gridId) => {
    if (!getToken()) {
      try {
        const fp  = _profileFp();
        const all = JSON.parse(localStorage.getItem(ZONES_KEY) || "[]")
          .filter((z) => !(z._fp === fp && z.gridId === gridId));
        localStorage.setItem(ZONES_KEY, JSON.stringify(all));
        setOwnedZones(withStringIds(all));
        purgeLegacyAICache(gridId);
      } catch {}
      return;
    }
    try {
      await deleteOwnedZone(gridId);
      setOwnedZones((prev) => prev.filter((z) => z.gridId !== gridId));
    } catch {}
  }, []);

  const clearZones = useCallback(async () => {
    if (!getToken()) {
      localStorage.removeItem(ZONES_KEY);
      localStorage.removeItem(PAID_KEY);
      localStorage.removeItem(AI_CACHE_KEY);
      setOwnedZones([]);
      return;
    }
    try {
      await clearOwnedZones();
      setOwnedZones([]);
    } catch {}
  }, []);

  const ownedIds = ownedZones.map((z) => z.gridId);

  return (
    <OwnedGridsContext.Provider value={{ ownedZones, ownedIds, saveZone, removeZone, clearZones, refresh, loaded }}>
      {children}
    </OwnedGridsContext.Provider>
  );
}

export function useOwnedGrids() {
  const ctx = useContext(OwnedGridsContext);
  if (!ctx) throw new Error("useOwnedGrids must be used inside OwnedGridsProvider");
  return ctx;
}

function _profileFp() {
  try {
    const p = JSON.parse(localStorage.getItem("landhep_profile") || "{}");
    return [p.bizCategory, p.bizSubtype, p.target_ses].map((v) => v || "").join("|");
  } catch { return ""; }
}
